import React from 'react';
import Link from 'next/link';
import { Article } from 'libs/microcms';
import { Image, Box, Text, Flex, AspectRatio, Breadcrumb, BreadcrumbItem } from '@chakra-ui/react';
import { formatDate } from 'utils/date';
import { paths } from 'constants/paths';
import { Share } from 'components/Blog/Share';
import { ContactSection } from 'components/Blog/ContactSection';
import { ChevronRightIcon } from '@chakra-ui/icons';

interface Props {
  article: Article;
}

const contentBodyCss = {
  h2: {
    margin: '48px 0 12px',
    fontSize: '34px',
  },
  h3: {
    margin: '32px 0 12px',
    fontSize: '24px',
  },
  'h2 + h3': {
    margin: '16px 0 12px',
  },
  img: {
    margin: '24px 0',
    width: '100%',
    borderRadius: '16px',
  },
  p: {
    margin: '24px 0',
    lineHeight: '28px',
  },
  ul: {
    margin: '12px 0',
  },
  ol: {
    margin: '12px 0',
  },
  a: {
    ':hover': {
      textDecoration: 'underline',
    },
  },
  '@media screen and (max-width: 30em)': {
    p: {
      fontSize: '14px',
    },
    h2: {
      fontSize: '20px',
    },
    h3: {
      fontSize: '18px',
    },
  },
};

export const BlogArticle: React.FC<Props> = ({ article }) => (
  <>
    <AspectRatio
      position="relative"
      display="flex"
      justifyContent="center"
      ratio={40 / 21}
      width="100%"
    >
      <Image borderRadius={{ md: '16px', base: 0 }} src={article.image.url} alt={article.title} />
    </AspectRatio>
    <Box
      maxWidth="740px"
      margin={{
        base: '0 auto',
      }}
      padding={{
        base: '0 24px',
        md: '0',
      }}
    >
      <Box m="32px 0">
        <Text as="h1" textStyle="h1" mb="16px" fontWeight="bold">
          {article.title}
        </Text>
        <Flex justifyContent="space-between">
          <Text textStyle="body2">{formatDate(article.publishedDate)}</Text>
          <Share
            title={article.title}
            url={process.env.ORIGIN + paths.blogDetail({ blogFriendlyId: article.slug })}
          />
        </Flex>
      </Box>
      <Box sx={contentBodyCss}>
        <div dangerouslySetInnerHTML={{ __html: article.body }} />
      </Box>
      <Breadcrumb
        separator={<ChevronRightIcon color="textGray.38" />}
        mt="48px"
        // 問い合わせセクションで無茶してるのでネガティブマージンが必要
        mb="-48px"
        fontSize="14px"
        noOfLines={1}
      >
        <BreadcrumbItem>
          <Link href="/" passHref>
            ホーム
          </Link>
        </BreadcrumbItem>

        <BreadcrumbItem>
          <Link href="/blog" passHref>
            導入実績
          </Link>
        </BreadcrumbItem>

        <BreadcrumbItem isCurrentPage color="textGray.38">
          <Link href={`/blog/${article.slug}`} passHref>
            {article.title}
          </Link>
        </BreadcrumbItem>
      </Breadcrumb>
    </Box>
    {!article.withoutContactLink && <ContactSection />}
  </>
);
